import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Textarea } from '../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Send, Loader2, ArrowLeft, Paperclip } from 'lucide-react';
import { toast } from 'react-toastify';
import { sendSupportEmailWithAttachment } from '../services/api'; // Novo endpoint de API
import Breadcrumb from '../components/Breadcrumb';

function SupportPage() {
    const { participanteNome, participanteEmail, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        assunto: '',
        mensagem: '',
    });
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    if (!isAuthenticated) {
        // Redireciona se não estiver autenticado (embora o App.js já faça isso)
        navigate('/login');
        return null;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.size > 5 * 1024 * 1024) { // Limite de 5MB
            toast.error("O arquivo é muito grande. O limite é de 5MB.");
            setFile(null);
            e.target.value = null;
            return;
        }
        setFile(selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.assunto || !formData.mensagem) {
            setError('Por favor, preencha o assunto e a mensagem.');
            return;
        }

        setIsSubmitting(true);

        const payload = new FormData();
        // Os nomes dos campos aqui devem corresponder aos @RequestParam no backend
        payload.append('assunto', formData.assunto);
        payload.append('mensagem', formData.mensagem);
        payload.append('nomeParticipante', participanteNome);
        payload.append('emailParticipante', participanteEmail); // CRÍTICO: Incluir o email

        if (file) {
            // O nome 'anexo' DEVE corresponder ao @RequestPart("anexo") no Java
            payload.append('anexo', file);
        }

        try {
            await sendSupportEmailWithAttachment(payload); 
            toast.success('Sua mensagem de suporte foi enviada com sucesso! Responderemos em breve.');
            navigate('/dashboard');
        } catch (err) {
            console.error('Erro ao enviar suporte:', err);
            setError(err.message || 'Ocorreu um erro ao tentar enviar a mensagem.');
            toast.error(err.message || 'Erro ao enviar mensagem de suporte.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Suporte e Ajuda' }
    ];

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-6">
                <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                </Button>
                <h1 className="text-3xl font-bold tracking-tight text-center sm:text-left w-full sm:w-auto">Suporte e Ajuda</h1>
            </div>

            <Card className="w-full max-w-3xl mx-auto">
                <CardHeader>
                    <CardTitle className="flex items-center mb-2"><Send className="mr-2 h-5 w-5" /> Enviar Pedido de Suporte</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Descreva o seu problema ou dúvida. Se possível, anexe uma imagem (screenshot) para ajudar a nossa equipa.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="assunto" className="mb-1">Assunto</Label>
                            <Input 
                                id="assunto" 
                                name="assunto" 
                                value={formData.assunto} 
                                onChange={handleChange} 
                                required 
                                disabled={isSubmitting} 
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="mensagem" className="mb-1">Mensagem / Descrição do Problema</Label>
                            <Textarea 
                                id="mensagem" 
                                name="mensagem" 
                                value={formData.mensagem} 
                                onChange={handleChange} 
                                rows={7}
                                required 
                                disabled={isSubmitting} 
                            />
                        </div>
                        
                        {/* Anexo de Arquivo - Ajustado para melhor responsividade */}
                        <div className="grid gap-2">
                            <Label htmlFor="file-upload" className="mb-1 flex items-center">
                                <Paperclip className="h-4 w-4 mr-2" /> Anexar Arquivo (Opcional, Max 5MB)
                            </Label>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                                <Input 
                                    id="file-upload" 
                                    type="file" 
                                    className="hidden" 
                                    onChange={handleFileChange} 
                                    ref={fileInputRef}
                                    accept="image/*, application/pdf"
                                    disabled={isSubmitting}
                                />
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={isSubmitting}
                                    className="w-full sm:w-auto flex-shrink-0" // w-full em mobile
                                >
                                    Selecionar Arquivo
                                </Button>
                                <span className="text-sm text-muted-foreground truncate w-full sm:w-auto sm:flex-grow">
                                    {file ? file.name : 'Nenhum arquivo selecionado.'}
                                </span>
                            </div>
                        </div>

                        <div className="text-xs text-muted-foreground pt-2">
                            <p>A sua mensagem será enviada a partir do email: <span className="font-semibold text-foreground">{participanteEmail}</span></p>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="secondary" onClick={() => navigate('/dashboard')} disabled={isSubmitting} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            Enviar Pedido
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default SupportPage;